import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import {
  adminUserOnboardingAnswersBeginningAction,
  adminUserOnboardingAnswersEndAction,
  adminUserOnboardingAnswersResultAction
} from "../Action/action";

const PHASE_META = {
  beginning: { title: "Start Answers", backLabel: "Onboarding answers" },
  end: { title: "End Answers", backLabel: "Onboarding answers" },
  result: { title: "Growth Summary", backLabel: "Onboarding answers" }
};

function SummaryList({ title, items }) {
  const list = Array.isArray(items) ? items.filter(Boolean) : [];
  if (!list.length) return null;
  return (
    <div style={{ marginTop: 20 }}>
      <h3 style={{ marginBottom: 8, fontSize: 15 }}>{title}</h3>
      <ul style={{ margin: 0, paddingLeft: 20 }}>
        {list.map((item, idx) => (
          <li key={`${title}-${idx}`} style={{ marginBottom: 6, whiteSpace: "pre-wrap" }}>
            {item}
          </li>
        ))}
      </ul>
    </div>
  );
}

export default function UserOnboardingAnswers() {
  const { userId, phase } = useParams();
  const navigate = useNavigate();
  const safePhase = ["beginning", "end", "result"].includes(String(phase)) ? String(phase) : null;

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [payload, setPayload] = useState(null);

  const meta = PHASE_META[safePhase] || { title: "Onboarding Answers", backLabel: "User" };

  useEffect(() => {
    if (!userId || !safePhase) {
      setLoading(false);
      setError("Invalid page.");
      return;
    }
    let cancelled = false;
    async function load() {
      setLoading(true);
      setError("");
      try {
        let res;
        if (safePhase === "beginning") res = await adminUserOnboardingAnswersBeginningAction(userId);
        else if (safePhase === "end") res = await adminUserOnboardingAnswersEndAction(userId);
        else res = await adminUserOnboardingAnswersResultAction(userId);

        if (cancelled) return;
        if (!res?.success) {
          setPayload(null);
          setError(res?.message || res?.msg || "Failed to load answers.");
          return;
        }
        setPayload(res.data || null);
      } catch (e) {
        if (cancelled) return;
        setPayload(null);
        setError(e?.response?.data?.message || e?.message || "Failed to load answers.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    load();
    return () => {
      cancelled = true;
    };
  }, [userId, safePhase]);

  const purchaseLabel = useMemo(() => {
    const p = payload?.purchase;
    if (!p) return null;
    return [p.course_title, p.id ? `Purchase #${p.id}` : null].filter(Boolean).join(" · ");
  }, [payload]);

  const items = Array.isArray(payload?.items) ? payload.items : [];
  const growth = payload?.growth_summary || null;

  return (
    <div className="content">
      <div className="flex items-center justify-between" style={{ marginBottom: 16, gap: 12, flexWrap: "wrap" }}>
        <div className="breadcrumb" style={{ marginBottom: 0 }}>
          <Link to="/users">Users</Link>
          <span>›</span>
          <Link to={`/users/${userId}`}>User</Link>
          <span>›</span>
          <span>{meta.title}</span>
        </div>
        <button type="button" className="btn btn-outline btn-sm" onClick={() => navigate(`/users/${userId}`)}>
          ← Back
        </button>
      </div>

      {error ? (
        <div className="banner banner-warning">
          <span className="banner-icon">⚠️</span>
          <div className="banner-text">
            <strong>Data unavailable.</strong> {error}
          </div>
        </div>
      ) : null}

      <div className="card">
        <div className="card-header">
          <h2>{meta.title}</h2>
        </div>
        {purchaseLabel ? <p className="help-text" style={{ marginBottom: 16 }}>{purchaseLabel}</p> : null}

        {loading ? (
          <div className="empty-state" style={{ padding: "28px 12px" }}>
            Loading…
          </div>
        ) : safePhase === "result" ? (
          growth ? (
            <div>
              {growth.generated_at ? (
                <p className="help-text" style={{ marginBottom: 12 }}>
                  Generated {growth.generated_at}
                </p>
              ) : null}
              {growth.snapshot_unavailable ? (
                <span className="badge badge-muted" style={{ marginBottom: 12, display: "inline-block" }}>
                  Snapshot unavailable
                </span>
              ) : null}
              <div style={{ whiteSpace: "pre-wrap", lineHeight: 1.55, marginBottom: 8 }}>
                {growth.summary || "No growth summary available."}
              </div>
              <SummaryList title="Strengths" items={growth.strengths} />
              <SummaryList title="Improvements" items={growth.improvements} />
              <SummaryList title="Next focus" items={growth.next_focus} />
            </div>
          ) : (
            <div className="empty-state" style={{ padding: "28px 12px" }}>
              No growth summary available.
            </div>
          )
        ) : (
          <div className="table-wrapper">
            <table>
              <thead>
                <tr>
                  <th>#</th>
                  <th>Question</th>
                  <th>Answer</th>
                </tr>
              </thead>
              <tbody>
                {items.length ? (
                  items.map((item, idx) => (
                    <tr key={`${item.source}-${item.question_id || idx}`}>
                      <td>{idx + 1}</td>
                      <td>
                        <strong>{item.question || "—"}</strong>
                      </td>
                      <td style={{ whiteSpace: "pre-wrap" }}>{item.answer || "—"}</td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={2}>
                      <div className="empty-state" style={{ padding: "28px 12px" }}>
                        No answers found for this phase.
                      </div>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
